import { Pill, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStackModalStore } from '../../../common/stores/modalStore';
import { useDeleteWorkspaceCoreValue } from '../services/useWorkspaceCoreValueQueries';

// WorkspaceCoreValue 타입 정의
interface WorkspaceCoreValue {
    id: string;
    name: string;
    workspace_id?: string;
    created_at?: string;
    updated_at?: string;
}

interface CoreValuePillProps {
    coreValue: WorkspaceCoreValue;
    workspaceId: string;
    onSuccess?: () => void;
    disabled?: boolean;
    deleteButtonText?: string;
    isEditable?: boolean; // 사용자 역할 추가
}

/**
 * 핵심 가치 Pill 컴포넌트
 * - 핵심 가치 표시 및 삭제 기능 포함
 * - 삭제 시 확인 모달 표시
 */
export const CoreValuePill: React.FC<CoreValuePillProps> = ({
    coreValue,
    workspaceId,
    onSuccess,
    disabled = false,
    deleteButtonText,
    isEditable,
}) => {
    const { t } = useTranslation('feedback');
    const [loading, setLoading] = useState(false);
    const { mutate: deleteCoreValue, isPending: isDeletingCoreValue } =
        useDeleteWorkspaceCoreValue();

    // 핵심 가치 삭제 핸들러
    const handleDeleteCoreValue = () => {
        if (!coreValue) return;

        // 모달 스택 시스템을 사용하여 확인 모달 표시
        const modalId = useStackModalStore
            .getState()
            .openModal('CONFIRMATION', {
                title: t('setting.coreValue.deleteConfirmTitle'),
                message: (
                    <Stack gap="sm">
                        <Text size="sm" c="dimmed" lineClamp={1}>
                            {t('setting.coreValue.deleteConfirmMessage', {
                                value: coreValue.name,
                            })}
                        </Text>
                        <Text
                            size="sm"
                            c="dimmed"
                            style={{
                                wordBreak: 'keep-all',
                            }}
                        >
                            {t('setting.coreValue.deleteNote')}
                        </Text>
                    </Stack>
                ),
                onConfirm: () => {
                    // 확인 버튼 클릭시 모달 닫고 API 호출
                    useStackModalStore.getState().closeModal(modalId);
                    deleteCoreValueAPI();
                },
                confirmText:
                    deleteButtonText || t('setting.coreValue.deleteConfirm'),
                cancelText: t('common.cancel'),
                confirmButtonColor: 'red',
            });
    };

    // 핵심 가치 삭제 API 호출
    const deleteCoreValueAPI = () => {
        setLoading(true);
        deleteCoreValue(
            {
                coreValueId: coreValue.id,
                workspaceId,
            },
            {
                onSuccess: () => {
                    notifications.show({
                        title: t('setting.coreValue.deleteSuccess'),
                        message: t('setting.coreValue.deleteSuccessMessage', {
                            value: coreValue.name,
                        }),
                        color: 'green',
                    });
                    // 성공 콜백 호출
                    onSuccess?.();
                },
                onError: error => {
                    // 에러 코드에 따라 다국어 메시지 적용
                    let errorMessage = error.message;
                    
                    // 에러 코드별 다국어 처리
                    if (error.message === 'NOT_PERMISSION_ERROR') {
                        errorMessage = t('error.notPermission');
                    } else if (error.message === 'NOT_FOUND_ERROR') {
                        errorMessage = t('error.notFound');
                    } 
                    
                    notifications.show({
                        title: t('setting.coreValue.deleteError'),
                        message: errorMessage,
                        color: 'red',
                    });
                },
                onSettled: () => {
                    setLoading(false);
                },
            }
        );
    };

    return (
        <Pill
            key={coreValue.id}
            variant="default"
            size="sm"
            withRemoveButton={isEditable} // 권한이 있는 경우에만 삭제 버튼 표시
            onRemove={isEditable ? handleDeleteCoreValue : undefined}
            disabled={disabled || loading || isDeletingCoreValue}
        >
            {coreValue.name}
        </Pill>
    );
};

export default CoreValuePill;
